import {Color} from '@benchling/nucleus-ui';
import {
  Button,
  CloseButton,
  createStyles,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {useClickOutside} from '@mantine/hooks';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';

import {PROGRESSIVE_DISCLOSURE_CATEGORIES, ProgressiveDisclosureCategory} from './ProgressiveDisclosureData';

interface ProgressiveDisclosureProps {
  onHoverOption: (prompt: string | null) => void;
  onSelectOption: (prompt: string) => void;
}

const useStyles = createStyles((theme) => ({
  container: {
    padding: theme.spacing.md,
  },
  optionItem: {
    display: 'block',
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: 0,
    fontSize: theme.fontSizes.md,
    color: Color.GRAY_900,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  optionsPanel: {
    borderRadius: theme.radius.xl,
    overflow: 'hidden',
  },
  divider: {
    borderColor: theme.colors.gray[2],
  },
}));

export default function ProgressiveDisclosure({
  onHoverOption,
  onSelectOption,
}: ProgressiveDisclosureProps): JSX.Element {
  const {classes} = useStyles();
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const lastHoveredOptionRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeCategory = PROGRESSIVE_DISCLOSURE_CATEGORIES.find((c) => c.id === activeCategoryId) ?? null;

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  const dismissPanel = useCallback(() => {
    clearHoverTimer();
    setActiveCategoryId(null);
    onHoverOption(null);
    lastHoveredOptionRef.current = null;
  }, [onHoverOption, clearHoverTimer]);

  const containerRef = useClickOutside<HTMLDivElement>(dismissPanel);

  const handleCategoryClick = useCallback((category: ProgressiveDisclosureCategory) => {
    setActiveCategoryId(category.id);
    lastHoveredOptionRef.current = null;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_PROGRESSIVE_DISCLOSURE_CATEGORY, {
      categoryId: category.id,
    });
  }, []);

  const handleOptionClick = useCallback(
    (categoryId: string, optionId: string, prompt: string) => {
      clearHoverTimer();
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_PROGRESSIVE_DISCLOSURE_OPTION, {
        categoryId,
        optionId,
      });
      onHoverOption(null);
      setActiveCategoryId(null);
      onSelectOption(prompt);
    },
    [onHoverOption, onSelectOption, clearHoverTimer]
  );

  return (
    <div ref={activeCategoryId != null ? containerRef : undefined} className={classes.container}>
      {activeCategory == null && (
        <Group spacing="sm" position="center">
          {PROGRESSIVE_DISCLOSURE_CATEGORIES.map((category) => (
            <Button
              key={category.id}
              variant="secondary"
              radius="xl"
              size="sm"
              onClick={() => {
                handleCategoryClick(category);
              }}
            >
              {category.label}
            </Button>
          ))}
        </Group>
      )}
      {activeCategory != null && (
        <Paper withBorder={true} className={classes.optionsPanel}>
          <Group position="apart" px="md" pt="sm" pb={4}>
            <Text size="sm" color="dimmed">
              {activeCategory.label}
            </Text>
            <CloseButton size="sm" onClick={dismissPanel} aria-label="Close suggestions" />
          </Group>
          <Stack spacing={0} pb="sm">
            {activeCategory.options.map((option, index) => (
              <React.Fragment key={option.id}>
                {index > 0 && <Divider className={classes.divider} />}
                <UnstyledButton
                  className={classes.optionItem}
                  onMouseEnter={() => {
                    const key = `${activeCategory.id}:${option.id}`;
                    hoverTimerRef.current = setTimeout(() => {
                      if (lastHoveredOptionRef.current !== key) {
                        lastHoveredOptionRef.current = key;
                        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.HOVERED_PROGRESSIVE_DISCLOSURE_OPTION, {
                          categoryId: activeCategory.id,
                          optionId: option.id,
                        });
                      }
                    }, 400);
                    onHoverOption(option.prompt);
                  }}
                  onMouseLeave={() => {
                    clearHoverTimer();
                    onHoverOption(null);
                  }}
                  onClick={() => {
                    handleOptionClick(activeCategory.id, option.id, option.prompt);
                  }}
                >
                  {option.label}
                </UnstyledButton>
              </React.Fragment>
            ))}
          </Stack>
        </Paper>
      )}
    </div>
  );
}
